import { Link } from '@asicsdigital/oneasics-blocks';
import parse, { domToReact } from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { useLegalUrls } from './use-legal-urls';

const racerosterPrivacyPolicyUrl = 'https://raceroster.com/legal/privacy-policy';

/**
 * React hook for handling copy with links.
 * Links we currently support:
 *   terms
 *   privacy-policy
 *   forgot-password
 *   oneasics-terms
 *
 * @param {String} view The view prefix you need for the copy key.
 * @param {Array[String]} copyKeys The list of copy keys needed to replace.
 */
export const useCopyWithLinks = ({ view, copyKeys = [] }) => {
  const {
    queryParams: { privacyHubUrl, style },
  } = useSelector((state) => state);
  const { asicsTermsUrl, oneasicsTermsUrl, privacyPolicyUrl } = useLegalUrls();
  const { t } = useTranslation(['overrides', ...(style ? [`${style}-views`] : []), 'views']);

  /* eslint-disable consistent-return */
  const copyReplacement = ({ attribs, children }) => {
    if (!attribs || !children) {
      return;
    }
    switch (attribs.id) {
      case 'ecomm-perf':
        return (
          <Link
            linkType="external"
            name="ecomm-perf"
            path="https://asics.com"
          >
            {domToReact(children)}
          </Link>
        );
      case 'ecomm-ot':
        return (
          <Link
            linkType="external"
            name="ecomm-ot"
            path="https://onitsukatiger.com"
          >
            {domToReact(children)}
          </Link>
        );
      case 'forgot-password':
        return (
          <Link
            name="forgot-password"
            path="forgot-password"
          >
            {domToReact(children)}
          </Link>
        );
      case 'login':
        return (
          <Link
            name="login"
            path="login"
          >
            {domToReact(children)}
          </Link>
        );
      case 'oneasics-terms':
        return (
          <Link
            newTab
            linkType="external"
            name="oneasics-terms"
            path={oneasicsTermsUrl}
          >
            {domToReact(children)}
          </Link>
        );
      case 'privacy-hub':
        return (
          <Link
            newTab
            linkType="external"
            name="privacy-hub"
            path={privacyHubUrl}
          >
            {domToReact(children)}
          </Link>
        );
      case 'privacy-policy':
        return (
          <Link
            newTab
            linkType="external"
            name="privacy-policy"
            path={privacyPolicyUrl}
          >
            {domToReact(children)}
          </Link>
        );
      case 'race-roster-privacy-policy':
        return (
          <Link
            newTab
            linkType="external"
            name="race-roster-privacy-policy"
            path={racerosterPrivacyPolicyUrl}
          >
            {domToReact(children)}
          </Link>
        );
      case 'set-password':
        return (
          <Link
            name="set-password"
            path="set-password"
          >
            {domToReact(children)}
          </Link>
        );
      case 'terms':
        return (
          <Link
            newTab
            linkType="external"
            name="terms-of-use"
            path={asicsTermsUrl}
          >
            {domToReact(children)}
          </Link>
        );
      default:
    }
  };
  /* eslint-enable consistent-return */

  const copyObj = {};
  copyKeys.filter(Boolean).forEach((copyKey) => {
    const viewPrefix = view ? `${view}.` : '';
    copyObj[copyKey] = parse(t(`${viewPrefix}${copyKey}`) || '', { replace: copyReplacement });
  });
  return copyObj;
};
